(() => {
  if (customElements.get('giveaway-form')) return;

  class GiveawayForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.querySelector('button[type="submit"]');
      this.submitLabel =
        this.submitButton?.querySelector('[data-button-text]') ||
        this.submitButton?.querySelector('span') ||
        this.submitButton;
      this.defaultLabel = this.submitLabel?.textContent?.trim() || '';
      this.isSubmitting = false;

      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this.onSubmit);
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      const values = this.collectValues(new FormData(this.form));
      const errors = this.validate(values);

      if (errors.length) {
        console.warn('Giveaway form validation failed:', errors);
        return;
      }

      this.send(values);
    }

    collectValues(formData) {
      const values = {
        age: [],
        preferences: [],
      };

      for (const [key, value] of formData.entries()) {
        if (key.startsWith('age')) {
          values.age.push(value);
        } else if (key.startsWith('preference')) {
          values.preferences.push(value);
        } else {
          values[key] = value;
        }
      }

      const countrySelect = this.form.querySelector('select[name="country"]');
      values.countryLabel =
        countrySelect?.selectedOptions?.[0]?.text?.trim() ||
        values.country ||
        '';

      return values;
    }

    validate(values) {
      const errors = [];

      if (!values.email) errors.push('email');
      if (!values.firstName) errors.push('firstName');
      if (!values.lastName) errors.push('lastName');
      if (!values.list_id && !values.listId) errors.push('list_id');
      if (!values.company_id && !values.companyId) errors.push('company_id');
      if (!values.countryLabel) errors.push('country');
      if (!values.age.length) errors.push('age');
      if (!values.preferences.length) errors.push('preferences');

      return errors;
    }

    buildPayload(values) {
      return {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  properties: {
                    preferences: values.preferences,
                    age: values.age,
                  },
                  location: {
                    country: values.countryLabel,
                  },
                  first_name: values.firstName,
                  last_name: values.lastName,
                  email: values.email,
                },
              },
            },
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: values.list_id || values.listId,
              },
            },
          },
        },
      };
    }

    async send(values) {
      const payload = this.buildPayload(values);
      const url = this.form.action;

      try {
        this.setSubmitting(true, 'Submitting…');

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            revision: '2024-07-15',
            'content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        this.form.reset();
        this.form.style.display = 'none';
        const successMessage = this.querySelector('.klaviyo-form__success');
        if (successMessage) {
          successMessage.style.display = 'block';
        }
      } catch (error) {
        console.error('Giveaway form submission failed:', error);
        this.setSubmitting(false, 'Try again');
      } finally {
        setTimeout(() => this.setSubmitting(false), 3000);
      }
    }

    setSubmitting(isSubmitting, label) {
      if (!this.submitButton) return;

      this.isSubmitting = isSubmitting;
      this.submitButton.toggleAttribute('disabled', isSubmitting);
      this.submitButton.classList.toggle('button--disabled', isSubmitting);

      if (this.submitLabel) {
        if (label) {
          this.submitLabel.textContent = label;
        } else if (!isSubmitting) {
          this.submitLabel.textContent = this.defaultLabel;
        }
      }
    }
  }

  customElements.define('giveaway-form', GiveawayForm);
})();
